import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { getIsChina } from "config";
import I18nHOC from "UIComponents/I18nHOC";
import classNames from "classnames";
import RadioButton from "UIComponents/RadioButton";
import { CreateDropDown } from "Attachment";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { isURL, getThumbUrl, htmlToText, addHttpsInUrl } from "Utils";
import { getIsReactPlayerVideoModalityEnabled } from "Utils/ACLStoreUtils";
import { isYoutubeLink } from "@toddle-edu/common-utils";
import { getWebLinkDetails, deleteAttachment } from "modules/Services";
import { createAIModalityAttachment } from "UIComponents/AIChat/modules/AIChatModules";
import {
  TextInput,
  Button,
  MediaCard,
  IconButton,
  Modal,
  Alert,
  Tabs,
} from "@toddle-edu/ds-web";
import {
  CaretDownFilled,
  DeleteOutlined,
  UploadOutlined,
  VideosOutlined,
} from "@toddle-edu/ds-icons";
import { getAIConversationAttachmentsQuery } from "UIComponents/AIChat/modules/AIChatQueries";
import classes from "./LinkifyModal.scss";

const DROP_DOWN_OPTIONS = [
  { key: "upload_device", label: "upload_device" },
  { key: "add_google_drive", label: "add_google_drive" },
  { key: "add_one_drive", label: "add_one_drive" },
];

const RADIO_BUTTONS = [
  { label: "common:add_url_uppercase", value: "URL" },
  { label: "common:upload_a_file", value: "FILE" },
];

const getVideoTabs = t => [
  { value: "URL", label: t("common:youtube_url") },
  { value: "FILE", label: t("common:upload_device") },
];

const VIDEO_DROP_DOWN_OPTIONS = [
  { key: "upload_device", label: "upload_device", accept: "video/*" },
  { key: "add_google_drive", label: "add_google_drive" },
  { key: "add_one_drive", label: "add_one_drive" },
];

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
};

const modalStyles = {
  modalBodyStyle: {
    padding: "8px 16px",
  },
  modalHeaderStyle: {
    padding: "12px 16px",
  },
  modalFooterStyle: {
    padding: "12px 16px",
  },
};

const buttonDropDownStyles = {
  buttonDropdownParentContainerStyle: {
    width: "100%",
  },
  buttonDropdownButtonComponentStyle: {
    height: "100%",
  },
  buttonDropDownContainerStyle: {
    top: "52px",
    width: "100%",
  },
};

const hoverContainerStyles = {
  width: "100%",
};

const inputRefs = {};
const EMPTY_PROGRESS = {};

const getRadioButtons = ({
  t,
  onClickRadioButton,
  selectedButton,
  isLoading,
  testId,
}) => {
  return (
    <div className={classes.radioButtonContainer}>
      {_.map(RADIO_BUTTONS, (button, index) => {
        return (
          <div
            className={classes.radioContainer}
            key={button.value}
            onClick={() => onClickRadioButton(button.value)}
          >
            <RadioButton
              dsVersion="2.0"
              label={t(button.label)}
              isChecked={selectedButton === button.value}
              color={"blue"}
              isDisabled={isLoading}
              testId={testId}
              size="medium"
            ></RadioButton>
          </div>
        );
      })}
    </div>
  );
};

const LinkifyModal = props => {
  const {
    id: idFromProp,
    url: urlFromProp,
    linkType: linkTypeFromProp,
    filename: filenameFromProp,
    mimeType: mimeTypeFromProp,
    fileType: fileTypeFromProp,
    onClickSave,
    onClose,
    t,
    setToastMsg,
    testId,
    customFilterFilesHandler,
    getWebLinkDetails,
    mode = "create",
    isVideoExplorerSelected = false,
    parentId,
    parentType = "AI_CONVERSATION",
    createAIModalityAttachment,
    deleteAttachment,
    isAttachmentAddedFromPrompt,
    progressOfUploads = {},
  } = props;

  let { isUrlOnly = false } = props;

  if (isVideoExplorerSelected) {
    isUrlOnly = true;
  }

  const showVideoTabs =
    isVideoExplorerSelected && getIsReactPlayerVideoModalityEnabled();
  const videoTabs = getIsChina()
    ? [{ value: "FILE", label: t("common:upload_device") }]
    : getVideoTabs(t);

  const isCancelledRef = useRef(false);

  const handleClose = () => {
    isCancelledRef.current = true;
    _.forEach(progressOfUploads, upload => {
      if (upload?.parentType === parentType && upload?.parentId === parentId) {
        upload?.cancelToken();
      }
    });
    onClose();
  };

  // states
  const [isLoading, setIsLoading] = useState(false);

  const [urlTypeState, setUrlTypeState] = useState({});
  const [fileTypeState, setFileTypeState] = useState({});
  const [linkType, setLinkType] = useState("URL");
  const [error, setError] = useState("");
  const [inputFieldErrors, setInputFieldErrors] = useState({
    url: "",
    title: "",
  });

  // 1 GB — applies only in the video explorer upload zone
  const VIDEO_MAX_SIZE_BYTES = 1073741824;

  const videoFilterFilesHandler = files => {
    const videoFiles = _.filter(files, file =>
      _.startsWith(file.type || file.mimeType, "video/")
    );
    // Apply 1 GB limit — do NOT delegate to customFilterFilesHandler which
    // enforces the 10 MB chat-attachment cap.
    const withinLimit = _.filter(
      videoFiles,
      file => (file.size || file.sizeBytes || 0) <= VIDEO_MAX_SIZE_BYTES
    );
    if (_.size(withinLimit) !== _.size(videoFiles)) {
      setError("File size exceeds the 1 GB limit.");
    }
    return withinLimit;
  };

  // vars
  let state = {};
  if (linkType === "URL") {
    state = urlTypeState;
  }
  if (linkType === "FILE") {
    state = fileTypeState;
  }
  const { filename, url, mimeType, fileType } = state;

  useEffect(() => {
    const lType =
      getIsChina() && isVideoExplorerSelected
        ? "FILE"
        : isUrlOnly
        ? "URL"
        : linkTypeFromProp || "URL";
    if (lType === "URL") {
      setUrlTypeState({
        url: urlFromProp,
      });
    } else if (lType === "FILE") {
      setFileTypeState({
        url: urlFromProp,
        filename: filenameFromProp,
        mimeType: mimeTypeFromProp,
        fileType: fileTypeFromProp,
      });
    }
    setLinkType(lType);
  }, [isUrlOnly]);

  const updateUrlTypeState = params => {
    setUrlTypeState(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const updateFileTypeState = params => {
    setFileTypeState(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const updateState = params => {
    switch (linkType) {
      case "URL": {
        updateUrlTypeState(params);
        break;
      }
      case "FILE": {
        updateFileTypeState(params);
        break;
      }
      default: {
        break;
      }
    }
  };

  const getURLAttachmentDetails = async () => {
    const url = _.trim(state.url);
    if (isURL(url)) {
      const linkUrl = addHttpsInUrl(url);
      const urlDetailObject = await getWebLinkDetails(linkUrl);

      if (urlDetailObject) {
        return _.first(urlDetailObject.attachments);
      }
    }

    return null;
  };

  const onSave = async () => {
    if (validateInputFields()) {
      let errors = 0;
      _.forEach(inputRefs, ref => {
        if (ref && !ref.value) {
          errors++;
        }
      });
      // validate if there is no file uploaded
      if (!_.trim(url)) {
        errors += 1;
        setError(t("common:please_upload_a_file"));
      }
      if (errors > 0) {
        setToastMsg({
          msg: "toastMsgs:please_fill_required_fields",
          type: "warning",
          position: "toast-top-center",
        });
        return;
      }

      // When showDisplayTextInput is false, we don't allow user to enter display text
      // so fetching URL details to get title, mimeType etc
      let urlAttachmentDetails;
      setIsLoading(true);
      if (linkType === "URL") {
        urlAttachmentDetails = await getURLAttachmentDetails();
      }

      if (isCancelledRef?.current) return;

      let params = {
        ...state,
        linkType,
        title:
          linkType === "URL" ? urlAttachmentDetails?.name : state?.filename,
        ...urlAttachmentDetails,
      };

      const isYoutubeUrl =
        (isUrlOnly || (isVideoExplorerSelected && linkType === "URL")) &&
        isYoutubeLink(url);

      // For youtube links in video explorer, create an attachment with transcript generation
      if (parentId && (isAttachmentAddedFromPrompt || isYoutubeUrl)) {
        // Delete existing attachment if it exists (for both create and update modes)
        if (idFromProp) {
          await deleteAttachment({
            attachmentId: idFromProp,
            refetchQueries: [
              {
                query: getAIConversationAttachmentsQuery,
                variables: { id: parentId },
              },
            ],
          });
        }

        const attachment = {
          url: state?.url,
          name: urlAttachmentDetails?.name || params?.title,
          type: params?.linkType === "URL" ? params?.type : params?.fileType,
        };

        const attachments = await createAIModalityAttachment(
          {
            ...attachment,
            parentId,
            parentType,
            metadata: {
              generateAiTranscript: true,
              isRevampEnabled: !getIsReactPlayerVideoModalityEnabled(),
            },
          },
          [
            {
              query: getAIConversationAttachmentsQuery,
              variables: { id: parentId },
            },
          ]
        );

        // Update params with created attachment data
        const createdAttachment = _.first(attachments);
        if (createdAttachment) {
          params = {
            ...createdAttachment,
            linkType,
            title: createdAttachment?.name,
            fileType: params?.fileType,
          };
        }
      }

      if (onClickSave) {
        await onClickSave(params);
      }

      setIsLoading(false);
    }
  };

  const onClickRadioButton = value => {
    setLinkType(value);
  };

  const onAddAttachment = params => {
    const attachment = _.get(params, "0", {});
    const { url, mimeType, name: filename, type } = attachment;
    updateState({ url, filename, mimeType, fileType: type });
    setError("");
  };

  const { thumbUrl, isIcon } = getThumbUrl({
    attachment: { ...state, type: state.fileType },
    width: 40,
    height: 40,
  });

  const validateInputFields = () => {
    if (linkType === "FILE") {
      setInputFieldErrors({ url: "", title: "" });
      return true;
    }
    if (!isURL(state?.url)) {
      setInputFieldErrors({
        title: "",
        url: t("common:web_link_not_valid"),
      });
      return false;
    } else if (isVideoExplorerSelected && !isYoutubeLink(state?.url)) {
      setInputFieldErrors({
        title: "",
        url: "Please enter a valid YouTube link.",
      });
      return false;
    } else {
      setInputFieldErrors({
        url: "",
        title: "",
      });
      return true;
    }
  };

  const getUrlContainer = () => {
    switch (linkType) {
      case "URL": {
        return (
          <div style={containerStyle}>
            <TextInput
              dsVersion="2.0"
              value={url}
              name={"url"}
              onChange={e => updateState({ url: e.target.value })}
              label={showVideoTabs ? `${t("common:link")}*` : t("common:link")}
              isRequired={!showVideoTabs}
              ref={ref => (inputRefs["url"] = ref)}
              placeholder={t("common:enter_link_here")}
              error={inputFieldErrors.url}
              size={"medium"}
              testId={`${testId}:url`}
              disabled={isLoading}
            />
            {inputFieldErrors.url && (
              <div className={classes.errorText}>{inputFieldErrors.url}</div>
            )}
          </div>
        );
      }
      case "FILE": {
        const isUploaded = url && mimeType;
        // eslint-disable-next-line no-restricted-properties
        const windowOpen = window.open;

        if (isUploaded) {
          return (
            <div className={classes.uploadButtonContainer}>
              <MediaCard
                dsVersion="2.0"
                cardType="medium"
                name={htmlToText(filename) || t("common:untitled")}
                type={fileType}
                mimeType={mimeType}
                url={url}
                thumbUrl={isIcon ? undefined : thumbUrl}
                showActionsOnHover={!showVideoTabs}
                onClick={() => windowOpen && windowOpen(url, "_blank").focus()}
                options={
                  <IconButton
                    dsVersion="2.0"
                    onClick={() => updateState({ url: "" })}
                    icon={<DeleteOutlined variant="subtle" size="xx-small" />}
                    variant="neutral"
                    shouldStopPropagation
                    disabled={isLoading}
                    testId={`${testId}:delete`}
                  />
                }
                testId={testId}
              />
            </div>
          );
        }

        const videoUploadTextClasses = classNames(
          classes.videoUploadText,
          "text-body-s"
        );

        const videoUploadMaxSizeClasses = classNames(
          classes.videoUploadMaxSize,
          "text-body-s"
        );

        if (showVideoTabs) {
          return (
            <div className={classes.videoUploadZone}>
              <div className={classes.videoUploadIconWrapper}>
                <VideosOutlined size="x-small" variant="subtle" />
              </div>
              <div className={videoUploadTextClasses}>
                {t("ai:supported_video_format")}
                <span className={videoUploadMaxSizeClasses}>
                  {" " + t("ai:max_file_size")}
                </span>
              </div>
              <CreateDropDown
                dropDownOptions={VIDEO_DROP_DOWN_OPTIONS}
                buttonComponent={
                  <Button
                    dsVersion="2.0"
                    size="small"
                    variant="neutral"
                    type="outlined"
                    disabled={isLoading}
                    icon={<UploadOutlined size="xx-small" />}
                    rightIcon={<CaretDownFilled size="x-small" />}
                    testId={`${testId}:create-dropdown`}
                  >
                    {t("common:upload_video")}
                  </Button>
                }
                disabled={isLoading}
                updateLoadingState={setIsLoading}
                addAttachment={onAddAttachment}
                {...buttonDropDownStyles}
                multiselect={false}
                hoverContainerStyles={hoverContainerStyles}
                customFilterFilesHandler={videoFilterFilesHandler}
                accept={"video/*"}
                isNewDropdown={true}
                dsVersion={"2.0"}
                parentId={parentId}
                parentType={parentType}
              />
              {error && <div className={classes.uploadError}>{error}</div>}
            </div>
          );
        }

        return (
          <div className={classes.uploadButtonContainer}>
            <CreateDropDown
              dropDownOptions={DROP_DOWN_OPTIONS}
              buttonComponent={
                <Button
                  dsVersion="2.0"
                  size="medium"
                  variant="neutral"
                  type="outlined"
                  disabled={isLoading}
                  isFullWidth
                  testId={`${testId}:create-dropdown`}
                >
                  {t("common:upload_with_label", {
                    label: t("common:lowercase", {
                      text: t("common:file"),
                    }),
                  })}
                </Button>
              }
              updateLoadingState={setIsLoading}
              addAttachment={onAddAttachment}
              {...buttonDropDownStyles}
              multiselect={false}
              hoverContainerStyles={hoverContainerStyles}
              customFilterFilesHandler={customFilterFilesHandler}
              isNewDropdown={true}
              dsVersion={"2.0"}
            />
            {error && <div className={classes.uploadError}>{error}</div>}
          </div>
        );
      }
    }
  };

  let headerTitle;
  if (isVideoExplorerSelected) {
    headerTitle = showVideoTabs
      ? t("common:attach_video")
      : t("common:add_youtube_link");
  } else if (mode === "create") {
    headerTitle = t("common:insert_with_label", {
      label: t("common:lowercase", { text: t("common:link") }),
    });
  } else {
    headerTitle = t("common:edit_link");
  }

  const modalHeight = isUrlOnly ? "fit-content" : "426px";

  return (
    <Modal
      dsVersion="2.0"
      onClose={handleClose}
      width="410px"
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      height={showVideoTabs ? "470px" : modalHeight}
    >
      <Modal.Header
        isClosable={true}
        hasSeparator={false}
        title={headerTitle}
      />

      <Modal.Body style={modalStyles.modalBodyStyle}>
        <div className={classes.modalBodyContainer}>
          {showVideoTabs ? (
            <div className={classes.tabsContainer}>
              <Tabs
                value={linkType}
                options={videoTabs}
                onChange={setLinkType}
              />
            </div>
          ) : (
            !isUrlOnly &&
            getRadioButtons({
              t,
              onClickRadioButton,
              selectedButton: linkType,
              isLoading,
              testId,
            })
          )}
          <div className={classes.inputsContainer}>{getUrlContainer()}</div>
        </div>
        {isVideoExplorerSelected && !(linkType === "FILE" && url) ? (
          <div className={classes.alertBox}>
            <Alert
              dsVersion={"2.0"}
              type={"info"}
              title={t("ai:video_exceeds_duration_limit")}
              message={t("ai:video_duration_limit_msg")}
              showIcon={true}
            />
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer
        style={modalStyles.modalFooterStyle}
        hasSeparator={showVideoTabs}
      >
        <footer className={classes.footerContainer}>
          <div className={classes.loaderContainer}>
            {isLoading ? (
              <div className={classes.analysisLoaderContainer}>
                <div className={classes.dot} />
                <div className="text-label-3">
                  {t("common:processing_no_dots")}
                </div>
              </div>
            ) : null}
          </div>
          <div className={classes.buttonsContainer}>
            <Button
              dsVersion="2.0"
              size="medium"
              onClick={handleClose}
              type="outlined"
              variant="neutral"
              testId={`${testId}:cancel`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              dsVersion="2.0"
              size="medium"
              disabled={isLoading || !url}
              onClick={onSave}
              variant={mode === "edit" ? "destructive" : "progressive"}
              testId={`${testId}:save`}
            >
              {mode === "edit" ? t("common:update") : t("common:done")}
            </Button>
          </div>
        </footer>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = state => ({
  progressOfUploads: _.get(
    state,
    "app_services.progressOfUploads",
    EMPTY_PROGRESS
  ),
});

const mapActionCreators = {
  setToastMsg,
  deleteAttachment,
  getWebLinkDetails,
  createAIModalityAttachment,
};

export default compose(
  I18nHOC({ resource: ["common", "ai"] }),
  connect(mapStateToProps, mapActionCreators)
)(LinkifyModal);
